import { useState } from "react";
import { Link } from "react-router";
import type { TaskDTO } from "@roomate/core";
import { useActors, useChiefActions, useMe, useProjects, useTasks, useUndoChiefAction } from "../api.ts";
import { Action, Page, PageHeader, Pill, Tile, Bento } from "../calm.tsx";
import {
  canUndoChiefAction,
  CHIEF_TOOL_LABEL,
  CHIEF_TRIGGER_KINDS,
  chiefRowNote,
  chiefRowState,
  chiefToolLabel,
  chiefTriggerView,
  subjectHref,
  type ChiefActionDTO,
  type ChiefActionFilters,
} from "../lib/chief.ts";
import { ErrorNote, formatAgo, inputCls, useNames } from "../ui.tsx";

/*
 * "Actions by the Chief" (docs/specs/chief-of-office.md §8.3): every act the Chief took, newest first.
 * A row reads time · what it did · to what · why · [Undo]. Anyone signed in reads it; members and admins undo.
 */

type Filters = { tool: string; projectId: string; undone: "" | "true" | "false"; trigger: string };
const NO_FILTERS: Filters = { tool: "", projectId: "", undone: "", trigger: "" };

const toQuery = (f: Filters): ChiefActionFilters => ({
  tool: f.tool || undefined,
  projectId: f.projectId || undefined,
  undone: f.undone || undefined,
  trigger: f.trigger || undefined,
});

export function ChiefPage() {
  const me = useMe().data!;
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const actions = useChiefActions({ ...toQuery(filters), limit: 200 });
  const projects = useProjects().data ?? [];
  const tasks = useTasks().data ?? [];
  const actors = useActors().data;
  const name = useNames(actors);
  const canUndo = canUndoChiefAction(me.access);
  const rows = actions.data ?? [];
  const filtered = Object.values(filters).some(Boolean);
  const set = (key: keyof Filters) => (e: React.ChangeEvent<HTMLSelectElement>) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Page>
      <PageHeader
        title="Actions by the Chief"
        meta={<span className="text-ink-3">Everything the Chief did, and why. Undo puts a change back unless someone has changed it since.</span>}
        actions={
          filtered ? (
            <Action onClick={() => setFilters(NO_FILTERS)} testId="chief-clear-filters">
              Clear filters
            </Action>
          ) : undefined
        }
      />

      <div className="mt-8 flex flex-wrap gap-2" role="group" aria-label="Filters" data-testid="chief-filters">
        <Select label="Tool" value={filters.tool} onChange={set("tool")} testId="chief-filter-tool">
          <option value="">All tools</option>
          {Object.entries(CHIEF_TOOL_LABEL).map(([tool, label]) => (
            <option key={tool} value={tool}>
              {label}
            </option>
          ))}
        </Select>
        <Select label="Project" value={filters.projectId} onChange={set("projectId")} testId="chief-filter-project">
          <option value="">All projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select label="Undone" value={filters.undone} onChange={set("undone")} testId="chief-filter-undone">
          <option value="">Undone or not</option>
          <option value="false">Not undone</option>
          <option value="true">Undone</option>
        </Select>
        <Select label="Trigger" value={filters.trigger} onChange={set("trigger")} testId="chief-filter-trigger">
          <option value="">Any trigger</option>
          {CHIEF_TRIGGER_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </Select>
      </div>

      <Bento className="mt-6">
        <Tile span={6} testId="chief-feed">
          {actions.isError ? (
            <ErrorNote error={actions.error} />
          ) : !actions.data ? (
            <p className="text-[13.5px] text-ink-3">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-[13.5px] text-ink-3" data-testid="chief-empty">
              {filtered ? "No actions match these filters." : "The Chief has not acted yet."}
            </p>
          ) : (
            <ol className="divide-y divide-rule/60">
              {rows.map((a) => (
                <ActionRow key={a.id} a={a} canUndo={canUndo} by={name} projectName={projects.find((p) => p.id === a.projectId)?.name} task={a.subjectType === "task" ? tasks.find((t) => t.id === a.subjectId) : undefined} />
              ))}
            </ol>
          )}
        </Tile>
      </Bento>
    </Page>
  );
}

function Select({ label, value, onChange, testId, children }: { label: string; value: string; onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void; testId: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[12px] text-ink-3">
      {label}
      <select className={inputCls} value={value} onChange={onChange} data-testid={testId}>
        {children}
      </select>
    </label>
  );
}

function ActionRow({ a, canUndo, by, projectName, task }: { a: ChiefActionDTO; canUndo: boolean; by: (id: string | null | undefined) => string; projectName: string | undefined; task: TaskDTO | undefined }) {
  const undo = useUndoChiefAction();
  const state = chiefRowState(a);
  const note = chiefRowNote(a);
  const trigger = chiefTriggerView(a.trigger);
  const subject = subjectHref(a.subjectType, a.subjectId);
  const detail = subjectDetail(a, task, by);
  return (
    <li className="py-4" data-testid="chief-action" data-state={state}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="w-24 shrink-0 text-[12.5px] tabular-nums text-ink-3" title={new Date(a.at).toLocaleString()}>
          {formatAgo(Date.now() - a.at)}
        </span>
        <span className="text-[14.5px] font-medium">{chiefToolLabel(a.tool)}</span>
        {subject ? (
          <Link to={subject} className="text-[13.5px] text-ink-2 hover:text-ink hover:underline" data-testid="chief-action-subject">
            {detail}
          </Link>
        ) : (
          <span className="text-[13.5px] text-ink-2">{detail}</span>
        )}
        {state === "undone" && <Pill testId="chief-action-undone">undone{a.undoneBy ? ` by ${by(a.undoneBy)}` : ""}</Pill>}
        {state === "changed" && <Pill tone="signal">changed since</Pill>}
        <span className="ml-auto flex items-center gap-2">
          {state === "undoable" && canUndo && (
            <Action onClick={() => undo.mutate(a.id)} disabled={undo.isPending} testId="chief-undo" label={`Undo: ${chiefToolLabel(a.tool)} ${detail}`.trim()}>
              {undo.isPending ? "Undoing…" : "Undo"}
            </Action>
          )}
        </span>
      </div>
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2" data-testid="chief-action-reason">
        {a.reason}
      </p>
      <p className="mt-1 text-[12px] text-ink-3">
        {projectName ? `${projectName} · ` : ""}
        {trigger.href ? (
          <Link to={trigger.href} className="hover:text-ink hover:underline" data-testid="chief-action-trigger">
            {trigger.label}
          </Link>
        ) : (
          <span data-testid="chief-action-trigger">{trigger.label}</span>
        )}
      </p>
      {note && (
        <p className="mt-1.5 text-[12.5px] text-ink-3" data-testid="chief-action-note">
          {note}
        </p>
      )}
      <ErrorNote error={undo.error} />
    </li>
  );
}

/** "to what": the task, run, schedule or person acted on, with the change when the call recorded one. */
function subjectDetail(a: ChiefActionDTO, task: TaskDTO | undefined, by: (id: string | null | undefined) => string): string {
  const input = (a.input ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof input[k] === "string" ? (input[k] as string) : null);
  if (a.subjectType === "task") {
    const label = task ? `#${task.number} ${task.title}` : "a task";
    const change = str("from") && str("to") ? `: ${str("from")} → ${str("to")}` : str("builderId") ? ` → ${by(str("builderId"))}` : "";
    return `${label}${change}`;
  }
  if (a.subjectType === "run") return "a run";
  if (a.subjectType === "schedule") return "a schedule";
  if (a.subjectType === "actor") return `to ${by(a.subjectId)}`;
  return "";
}
